import { useRef, useState, useEffect, useCallback } from 'react'
import {
  View,
  Platform,
  TextInput as RNTextInput,
  Text,
  useWindowDimensions,
  type LayoutChangeEvent,
} from 'react-native'
import { Pressable } from './Pressable'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSpring,
  withRepeat,
  withSequence,
  interpolateColor,
  useReducedMotion,
  FadeIn,
  FadeOut,
} from 'react-native-reanimated'
import { impact, ImpactStyle } from '@/lib/utils/haptics'
import { scheduleIdleTask } from '@/lib/utils/idle-callback'
import { COLORS, FONTS, RADIUS, SPACING } from '../../lib/brand'
import { useTheme } from '../../lib/hooks/use-theme'
import { useFontScale } from '../../lib/hooks/use-font-scale'
import { useSemanticColors, DURATION, INTERACTION } from '../../lib/theme/tokens'
import { useShakeAnimation } from '../../lib/hooks/use-shake-animation'
import { useTranslation } from 'react-i18next'

import { OTP_RESEND_LOCKOUT_SECONDS } from '@/lib/services/phone-verification.service'

interface OtpInputProps {
  length?: number
  /** Resolve on completion; reject after presenting a verification failure. */
  onComplete: (code: string) => Promise<void>
  error?: string
  disabled?: boolean
  /**
   * Segundos hasta que "Reenviar" se vuelve a poder tocar.
   *
   * El default NO es un número escrito acá: es el mismo
   * `OTP_RESEND_LOCKOUT_SECONDS` que el servidor aplica. Era 60 mientras el
   * servidor enfriaba 120, así que el botón se encendía a la mitad del
   * enfriamiento, la persona lo tocaba y recibía un 429 por hacer exactamente
   * lo que la pantalla la acababa de invitar a hacer. Un contador que miente
   * es peor que no tener contador: el que no lo tiene al menos no promete.
   */
  resendCooldown?: number
  /** Return a promise so digits/timer only reset after a successful resend. */
  onResend?: () => Promise<void>
  testID?: string
}

interface Palette {
  boxBg: string
  boxBorderEmpty: string
  textPrimary: string
  trackBg: string
  countdownMuted: string
}

/**
 * The digit's own type. React Native scales BOTH of these by the OS text
 * multiplier; the cell around them was raw dp and scaled by nothing, which is
 * the whole defect — at 2.0 the code the user came here to read stayed
 * visually small inside a box built for 1.0 while every label around it had
 * doubled.
 */
const DIGIT_FONT_SIZE = 20
const DIGIT_LINE_HEIGHT = 24

function DigitBox({
  digit,
  active,
  error,
  disabled,
  width,
  height,
  palette,
}: {
  digit: string
  active: boolean
  error?: string
  disabled?: boolean
  width: number
  height: number
  palette: Palette
}) {
  const pulse = useSharedValue(0)
  const reducedMotion = useReducedMotion()

  useEffect(() => {
    if (active) {
      pulse.value = withRepeat(
        withSequence(
          withTiming(1, { duration: DURATION.verySlow }),
          withTiming(0, { duration: DURATION.verySlow }),
        ),
        -1,
        true,
      )
    } else {
      pulse.value = withTiming(0, { duration: DURATION.fast })
    }
  }, [active, pulse])

  /**
   * The digit's entrance, animated WITHOUT mounting anything.
   *
   * The previous version swapped each cell between a plain element and an
   * `Animated.View` wrapper on every keystroke, so React REMOUNTED the cell —
   * and restarted its entering animation — six times per code. On iOS that
   * mount churn left cells stuck mid-enter, which reads exactly as "I typed
   * and nothing updated". A shared value moves the same node instead: no
   * remount, no entering/exiting, nothing to get stuck.
   */
  const pop = useSharedValue(digit ? 1 : 0)
  const prevDigit = useRef(digit)

  useEffect(() => {
    const appeared = Boolean(digit) && digit !== prevDigit.current
    const disappeared = !digit && Boolean(prevDigit.current)
    prevDigit.current = digit

    if (reducedMotion) {
      pop.value = digit ? 1 : 0
      return
    }
    if (appeared) {
      pop.value = 0
      pop.value = withSpring(1, { damping: 15, stiffness: 300 })
    } else if (disappeared) {
      pop.value = withTiming(0, { duration: DURATION.instant })
    }
  }, [digit, pop, reducedMotion])

  const digitStyle = useAnimatedStyle(() => ({
    opacity: pop.value,
    transform: [{ scale: 0.72 + 0.28 * pop.value }],
  }))

  const boxBorderStyle = useAnimatedStyle(() => {
    if (error) return { borderColor: COLORS.coral.DEFAULT }
    if (digit) return { borderColor: COLORS.coral.DEFAULT }
    if (active) {
      return {
        borderColor: interpolateColor(
          pulse.value,
          [0, 1],
          [COLORS.coral.DEFAULT, COLORS.coral.light],
        ),
      }
    }
    return { borderColor: palette.boxBorderEmpty }
  })

  return (
    <Animated.View
      accessible={false}
      style={[
        {
          width,
          minWidth: 44,
          height,
          minHeight: 44,
          borderRadius: RADIUS.md,
          backgroundColor: palette.boxBg,
          borderWidth: 1,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: disabled ? 0.56 : 1,
        },
        boxBorderStyle,
      ]}
    >
      <Animated.Text
        style={[
          {
            color: palette.textPrimary,
            fontSize: DIGIT_FONT_SIZE,
            lineHeight: DIGIT_LINE_HEIGHT,
            fontFamily: FONTS.bodyBold,
            textAlign: 'center',
          },
          digitStyle,
        ]}
      >
        {digit || null}
      </Animated.Text>
    </Animated.View>
  )
}

export function OtpInput({
  length = 6,
  onComplete,
  error,
  disabled,
  resendCooldown = OTP_RESEND_LOCKOUT_SECONDS,
  onResend,
  testID,
}: OtpInputProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { width: windowWidth } = useWindowDimensions()
  const { box } = useFontScale()
  const isNarrow = windowWidth < 380
  const cellGap = isNarrow ? SPACING.xs : SPACING.sm
  const baseCellWidth = isNarrow ? 44 : 48
  const baseCellHeight = isNarrow ? 52 : 56

  // The row is measured rather than assumed, because width is the one
  // dimension here with a hard ceiling: `length` cells and `length - 1` gaps
  // have to fit the container, and `box(48)` at 2.0 is 96 — six of those is
  // 576dp inside a 448dp phone. So the cell grows toward the scaled size and
  // stops at its share of the row.
  const [rowWidth, setRowWidth] = useState(0)
  const handleRowLayout = useCallback((e: LayoutChangeEvent) => {
    const next = e.nativeEvent.layout.width
    setRowWidth((prev) => (Math.abs(prev - next) < 1 ? prev : next))
  }, [])

  // Height has no such ceiling, so it grows by exactly the amount the digit's
  // line box grew. That keeps the padding around the digit constant instead of
  // letting the glyph eat it — and at scale 1 the delta is 0, so the cell is
  // the approved 56 to the pixel.
  const cellHeight = baseCellHeight + (box(DIGIT_LINE_HEIGHT) - DIGIT_LINE_HEIGHT)
  const widthShare =
    rowWidth > 0 ? Math.floor((rowWidth - cellGap * (length - 1)) / length) : baseCellWidth
  // `Math.max(..., baseCellWidth)` is the no-regression floor: a scale-1 user
  // gets exactly 48 (or 44 when narrow) as before, and nothing here can ever
  // make a cell smaller than the size the owner approved.
  const cellWidth = Math.max(Math.min(box(baseCellWidth), widthShare), baseCellWidth)

  // Theme-aware palette. On the desktop auth layout the form column can be
  // light while the app theme is dark, so the boxes inherit semantic tokens
  // from a single branch instead of each cell deciding independently.
  const palette: Palette = {
    boxBg: isDark ? 'rgba(255,255,255,0.08)' : colors.surfaceSubtle,
    boxBorderEmpty: isDark ? 'rgba(255,255,255,0.20)' : colors.border,
    textPrimary: colors.textPrimary,
    trackBg: isDark ? 'rgba(255,255,255,0.12)' : colors.border,
    countdownMuted: colors.textMuted,
  }

  const [code, setCode] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [countdown, setCountdown] = useState(resendCooldown)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const requestInFlight = useRef(false)
  const inputDisabled = Boolean(disabled || submitting || resending)
  const inputRef = useRef<RNTextInput | null>(null)
  const prevError = useRef<string | undefined>(undefined)
  const lastCompletedCode = useRef<string | null>(null)

  const digits = Array.from({ length }, (_, i) => code[i] ?? '')
  const activeIndex = Math.min(code.length, length - 1)

  const setInputRef = useCallback((ref: unknown) => {
    inputRef.current = ref as RNTextInput | null
  }, [])

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }, [])

  // Auto-focus the (invisible) input when the OTP step appears.
  //
  // A single `requestAnimationFrame` focus is not enough on iOS: the screen
  // pushes in over the input, and UIKit silently drops `becomeFirstResponder`
  // mid-transition. The user then lands on a screen with no keyboard and no
  // visible field — "no te deja escribirlo". Wait for the interaction to
  // settle and then retry until the input reports itself focused, so the
  // number pad is up by the time the person is ready to type.
  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const attempt = (remaining: number) => {
      if (cancelled || disabled) return
      const el = inputRef.current
      if (el?.isFocused()) return
      el?.focus()
      if (remaining > 0) timer = setTimeout(() => attempt(remaining - 1), 350)
    }

    const cancelScheduledFocus = scheduleIdleTask(() => attempt(3), {
      minimumDelayMs: 120,
      timeoutMs: 750,
    })

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      cancelScheduledFocus()
    }
    // Mount-only. OTP steps mount after the send resolves, so `disabled` is
    // false here; if it ever mounts locked, the retries simply no-op until
    // the disabled→false effect below takes over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // On iOS, `editable={false}` dismisses the keyboard, and nothing brings it
  // back on its own when the field re-enables — so after a failed verify (or
  // the two-step phone-change flow's step 1 → step 2) the boxes sit there dead
  // and the person taps into nothing. Refocus when the lock lifts.
  const prevDisabled = useRef(inputDisabled)
  useEffect(() => {
    const was = prevDisabled.current
    prevDisabled.current = inputDisabled
    if (was && !inputDisabled) focusInput()
  }, [inputDisabled, focusInput])

  useEffect(() => {
    if (countdown <= 0) return
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000)
    return () => clearInterval(timer)
  }, [countdown])

  const countdownProgress = useSharedValue(1)

  useEffect(() => {
    if (countdown === resendCooldown) {
      countdownProgress.value = 1
      countdownProgress.value = withTiming(0, { duration: resendCooldown * 1000 })
    }
  }, [countdown === resendCooldown, resendCooldown, countdownProgress]) // eslint-disable-line react-hooks/exhaustive-deps

  const progressBarStyle = useAnimatedStyle(() => ({
    width: `${countdownProgress.value * 100}%` as `${number}%`,
  }))

  const flashProgress = useSharedValue(0)

  const flashBoxes = useCallback(() => {
    flashProgress.value = withSequence(
      withTiming(1, { duration: DURATION.fast }),
      withTiming(0, { duration: DURATION.normal }),
    )
  }, [flashProgress])

  const { animatedStyle: shakeStyle, shake } = useShakeAnimation()

  useEffect(() => {
    if (error && !prevError.current) {
      shake()
      // Keep the code available for the explicit retry action or another paste.
      lastCompletedCode.current = null
      focusInput()
    }
    prevError.current = error
  }, [error, focusInput, shake])

  const submitCode = useCallback(
    async (next: string) => {
      if (disabled || requestInFlight.current || lastCompletedCode.current === next) return
      requestInFlight.current = true
      lastCompletedCode.current = next
      setSubmitting(true)
      try {
        flashBoxes()
        await onComplete(next)
      } catch {
        // The screen owns the message; a rejected attempt may use the same code.
        lastCompletedCode.current = null
      } finally {
        requestInFlight.current = false
        setSubmitting(false)
      }
    },
    [disabled, flashBoxes, onComplete],
  )

  const handleChange = useCallback(
    (text: string) => {
      if (disabled || requestInFlight.current) return
      const next = text.replace(/\D/g, '').slice(0, length)

      if (next.length > code.length) {
        impact(ImpactStyle.Light)
      }

      setCode(next)
      if (next.length < length) {
        lastCompletedCode.current = null
      }

      if (next.length === length) void submitCode(next)
    },
    [code, disabled, length, submitCode],
  )

  const handleResend = useCallback(async () => {
    if (!onResend || disabled || countdown > 0 || requestInFlight.current) return
    requestInFlight.current = true
    setResending(true)
    try {
      await onResend()
      setCountdown(resendCooldown)
      setCode('')
      lastCompletedCode.current = null
      focusInput()
    } catch {
      // The screen reports failure. The current challenge remains usable.
    } finally {
      requestInFlight.current = false
      setResending(false)
    }
  }, [countdown, disabled, focusInput, onResend, resendCooldown])

  return (
    // `onLayout` sits on the ROOT, not on the row: everything below it is
    // content-sized by `alignItems: 'center'`, so measuring the row would just
    // hand back the sum of the cells it already contains. The root is the only
    // node here that reports the width actually available to spend.
    <View style={{ alignItems: 'center' }} testID={testID} onLayout={handleRowLayout}>
      <Animated.View style={shakeStyle}>
        <View style={{ marginBottom: SPACING.md }}>
          <View
            accessible={false}
            style={{
              flexDirection: 'row',
              justifyContent: 'center',
              gap: cellGap,
            }}
          >
            {digits.map((digit, i) => (
              <DigitBox
                key={i}
                digit={digit}
                active={isFocused && i === activeIndex}
                error={error}
                disabled={inputDisabled}
                width={cellWidth}
                height={cellHeight}
                palette={palette}
              />
            ))}
          </View>

          <RNTextInput
            ref={setInputRef}
            value={code}
            onChangeText={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            editable={!inputDisabled}
            keyboardType="number-pad"
            inputMode="numeric"
            textContentType="oneTimeCode"
            autoComplete={Platform.OS === 'android' ? 'sms-otp' : 'one-time-code'}
            importantForAutofill="yes"
            autoCorrect={false}
            autoCapitalize="none"
            caretHidden
            selectionColor={COLORS.coral.DEFAULT}
            disableFullscreenUI
            accessibilityLabel={t('a11y.otp_code')}
            testID={testID ? `${testID}.field` : undefined}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              opacity: 0.01,
              color: palette.textPrimary,
              fontSize: 16,
              fontFamily: FONTS.body,
              textAlign: 'center',
            }}
          />
        </View>
      </Animated.View>

      {error ? (
        <Animated.Text
          entering={FadeIn.duration(DURATION.fast)}
          exiting={FadeOut.duration(DURATION.fast)}
          style={{
            color: colors.textAccent,
            fontSize: 14,
            fontFamily: FONTS.body,
            marginBottom: SPACING.sm + SPACING.xs,
          }}
        >
          {error}
        </Animated.Text>
      ) : null}

      {error ? (
        code.length === length ? (
          <Pressable
            onPress={() => submitCode(code)}
            disabled={inputDisabled}
            accessibilityRole="button"
            accessibilityLabel={t('common.retry')}
            accessibilityState={{ disabled: inputDisabled }}
            testID={testID ? `${testID}.retry` : undefined}
            style={{
              minHeight: INTERACTION.minTouchTarget,
              minWidth: INTERACTION.minTouchTarget,
              paddingHorizontal: SPACING.md,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text
              numberOfLines={1}
              adjustsFontSizeToFit
              style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textAccent }}
            >
              {t('common.retry')}
            </Text>
          </Pressable>
        ) : null
      ) : null}

      {onResend && (
        <View style={{ alignItems: 'center' }}>
          <Pressable
            onPress={handleResend}
            disabled={countdown > 0 || inputDisabled}
            accessibilityRole="button"
            accessibilityLabel={t('auth.resend_code')}
            accessibilityState={{ disabled: countdown > 0 || inputDisabled }}
            testID={testID ? `${testID}.resend` : undefined}
            hitSlop={INTERACTION.iconHitSlop}
            style={{
              minHeight: INTERACTION.minTouchTarget,
              minWidth: INTERACTION.minTouchTarget,
              paddingHorizontal: SPACING.md,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text
              style={{
                fontSize: 14,
                fontFamily: FONTS.body,
                color: countdown > 0 ? palette.countdownMuted : colors.textAccent,
              }}
            >
              {countdown > 0 ? t('auth.resend_in', { seconds: countdown }) : t('auth.resend_code')}
            </Text>
          </Pressable>
          {countdown > 0 && (
            <View
              style={{
                width: 128,
                height: 2,
                backgroundColor: palette.trackBg,
                borderRadius: RADIUS.full,
                marginTop: SPACING.xs + 2,
                overflow: 'hidden',
              }}
            >
              <Animated.View
                style={[
                  progressBarStyle,
                  {
                    height: '100%',
                    backgroundColor: `${COLORS.coral.DEFAULT}99`, // ~0.6 alpha
                    borderRadius: RADIUS.full,
                  },
                ]}
              />
            </View>
          )}
        </View>
      )}
    </View>
  )
}
